<template>
  <div class="product-detail-root">
    <AppNavbar />

    <!-- BREADCRUMB + BACK -->
    <div class="top-nav">
      <div class="top-nav-inner">
        <nav class="breadcrumb">
          <router-link to="/" class="crumb-link">Home</router-link>
          <span class="sep">/</span>
          <router-link to="/shop" class="crumb-link">Shop</router-link>
          <span class="sep">/</span>
          <span class="leaf">{{ product?.name || "Product Detail" }}</span>
        </nav>
        <router-link to="/shop" class="back-link">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2.5"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <line x1="19" y1="12" x2="5" y2="12"></line>
            <polyline points="12 19 5 12 12 5"></polyline>
          </svg>
          Back to Shop
        </router-link>
      </div>
    </div>

    <!-- PAGE BODY -->
    <main class="page-body">
      <p v-if="isLoading" class="status-text centered">
        Loading product detail...
      </p>
      <p v-else-if="errorMessage" class="error-text centered">
        {{ errorMessage }}
      </p>

      <div v-else-if="product" class="detail-card">
        <!-- IMAGE COLUMN -->
        <div class="image-col">
          <div class="main-image-wrap">
            <span class="badge-sale" v-if="product.isOnSale">Sale</span>
            <img
              v-if="activeImage"
              :src="activeImage"
              :alt="product.name || 'Product Image'"
            />
            <div v-else class="no-image">No Image Available</div>
          </div>

          <div class="thumbnail-row" v-if="productImageList.length > 1">
            <div
              v-for="(img, i) in productImageList"
              :key="i"
              class="thumb"
              :class="{ active: activeImage === img }"
              @click="activeImage = img"
            >
              <img :src="img" :alt="`View ${i + 1}`" />
            </div>
          </div>

          <!-- DISCLAIMER -->
          <div class="image-disclaimer">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="13"
              height="13"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <circle cx="12" cy="12" r="10"></circle>
              <line x1="12" y1="8" x2="12" y2="12"></line>
              <line x1="12" y1="16" x2="12.01" y2="16"></line>
            </svg>
            <span
              ><strong>Disclaimer:</strong> The images shown are AI-generated
              for illustrative purposes only. Actual product colors, materials,
              and fine details may vary slightly from what is displayed
              here.</span
            >
          </div>
        </div>

        <!-- INFO COLUMN -->
        <div class="info-col">
          <div class="product-header">
            <h2 class="product-title">{{ product.name || "Product" }}</h2>
            <div class="price-row">
              <span class="net-price">{{
                formatRs(product.net_price, product)
              }}</span>
              <span class="mrp-price" v-if="showStruckMrp">{{
                formatRs(product.mrp, product)
              }}</span>
            </div>
            <span
              v-if="
                showCv &&
                product.coinValue !== undefined &&
                product.coinValue !== null
              "
              class="coin-value-pill"
            >
              CV: <strong>{{ formatNumericValue(product.coinValue) }}</strong>
            </span>
            <span
              v-if="Number(product.shippingCharge) > 0"
              class="coin-value-pill"
            >
              Shipping: <strong>{{ formatRs(product.shippingCharge) }}</strong>
            </span>
          </div>

          <!-- CART ACTION (moved above description) -->
          <div class="cart-action-block">
            <div v-if="cartQuantity === 0" class="action-buttons-row">
              <button
                class="add-to-cart-btn"
                :class="{ adding: isAddingToCart }"
                :disabled="isAddingToCart"
                @click="handleAddToCart"
              >
                <!-- disabled -->
                <svg
                  v-if="!isAddingToCart"
                  xmlns="http://www.w3.org/2000/svg"
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2.5"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                >
                  <circle cx="9" cy="21" r="1"></circle>
                  <circle cx="20" cy="21" r="1"></circle>
                  <path
                    d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"
                  ></path>
                </svg>
                {{ isAddingToCart ? "Adding..." : "Add to Cart" }}
              </button>

              <!-- <button class="buy-now-btn" @click="handleBuyNow">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2.5"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                >
                  <polyline points="9 11 12 14 22 4"></polyline>
                  <path
                    d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"
                  ></path>
                </svg>
                Buy Now
              </button> -->
            </div>

            <div v-else class="qty-state">
              <div class="qty-controls">
                <button class="qty-btn" @click="decreaseQty">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="14"
                    height="14"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2.5"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  >
                    <line x1="5" y1="12" x2="19" y2="12"></line>
                  </svg>
                </button>
                <span class="qty-count">{{
                  formatNumericValue(cartQuantity)
                }}</span>
                <button class="qty-btn" @click="increaseQty">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="14"
                    height="14"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2.5"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  >
                    <line x1="12" y1="5" x2="12" y2="19"></line>
                    <line x1="5" y1="12" x2="19" y2="12"></line>
                  </svg>
                </button>
              </div>
              <span class="qty-label">in cart</span>

              <!-- <button class="buy-now-btn" @click="handleBuyNow">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2.5"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                >
                  <polyline points="9 11 12 14 22 4"></polyline>
                  <path
                    d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"
                  ></path>
                </svg>
                Buy Now
              </button> -->
            </div>

            <transition name="toast-fade">
              <div v-if="showToast" class="toast">
                ✓ Added to cart successfully
              </div>
            </transition>
          </div>

          <!-- DESCRIPTION with Read More / Collapse -->
          <div class="description-block" v-if="descriptionParts.mainText">
            <p
              class="product-description"
              :class="{ clamped: !descExpanded && isDescriptionLong }"
            >
              {{ descriptionParts.mainText }}
            </p>

            <!-- Chain disclaimer — exact-match note, pendant-only.
       Only shown once expanded, right below the paragraph. -->
            <template v-if="descExpanded">
              <p
                v-for="(note, idx) in descriptionParts.noteLines"
                :key="`note-${idx}`"
                class="product-note"
              >
                {{ note }}
              </p>
            </template>

            <!-- MANUFACTURING / ORIGIN META -->
            <dl
              v-if="descExpanded && descriptionParts.metaFields.length"
              class="product-meta-list"
            >
              <div
                v-for="(field, idx) in descriptionParts.metaFields"
                :key="idx"
                class="product-meta-row"
              >
                <dt class="product-meta-label">{{ field.label }}:</dt>
                <dd class="product-meta-value">{{ field.value }}</dd>
              </div>
            </dl>

            <!-- READ MORE / SHOW LESS — now sits at the bottom, below all the
       expanded content (chain note + origin/manufacturer/marketer),
       so it reads as "collapse everything" rather than sitting
       awkwardly mid-block. -->
            <button
              v-if="isDescriptionLong"
              class="desc-toggle"
              @click="descExpanded = !descExpanded"
            >
              {{ descExpanded ? "Show less" : "Read more" }}
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="13"
                height="13"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2.5"
                stroke-linecap="round"
                stroke-linejoin="round"
                class="desc-toggle-icon"
                :class="{ rotated: descExpanded }"
              >
                <polyline points="6 9 12 15 18 9"></polyline>
              </svg>
            </button>
            <!-- DELIVERY TIMELINE — hardcoded, shown for every product -->
            <div class="delivery-timeline">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              >
                <rect x="1" y="3" width="15" height="13"></rect>
                <polygon points="16 8 20 8 23 11 23 16 16 16 16 8"></polygon>
                <circle cx="5.5" cy="18.5" r="2.5"></circle>
                <circle cx="18.5" cy="18.5" r="2.5"></circle>
              </svg>
              <span>Estimated Delivery: <strong>25 - 30 Days</strong></span>
            </div>
          </div>

          <!-- SPECIFICATIONS -->
          <template v-if="productSpecifications.length">
            <div class="divider"></div>
            <div class="specs-block">
              <h4 class="specs-title">Specifications</h4>
              <div class="spec-list">
                <div
                  v-for="([key, value], specIndex) in productSpecifications"
                  :key="`${key}-${specIndex}`"
                  class="spec-item"
                >
                  <span class="spec-key">{{ key }}</span>
                  <span class="spec-value">{{ value }}</span>
                </div>
              </div>
            </div>
          </template>
        </div>
      </div>

      <p v-else class="status-text centered">No product data found.</p>
    </main>

    <Footer />
  </div>
</template>

<script setup>
import { computed, onMounted, ref, watch } from "vue";
import { useRoute } from "vue-router";
import { checkTenant, getProductDetailById } from "rdep-ecom-sdk";
import { useAppStore } from "../../stores/useAppStore.js";
import { useCartStore } from "../../stores/useCartStore.js";
import AppNavbar from "../AppNavbar.vue";
import Footer from "../FooterView.vue";

const route = useRoute();
// const router = useRouter();
const appStore = useAppStore();
const cartStore = useCartStore();

const product = ref(null);
const isLoading = ref(false);
const errorMessage = ref("");
const resolvedTenantId = ref("");
const isAddingToCart = ref(false);
const cartQuantity = ref(0);
const showToast = ref(false);
const activeImage = ref("");
const descExpanded = ref(false);

function normalizeRoleToken(value) {
  return String(value || "")
    .trim()
    .toUpperCase()
    .replace(/[^A-Z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");
}

// --- COMPUTED ---
const userRoleToken = computed(() => {
  try {
    const rawAuth = window.localStorage.getItem("auth.currentUser");
    const authPayload = rawAuth ? JSON.parse(rawAuth) : {};
    const user = authPayload?.data || authPayload || {};
    const roleCandidates = [
      user?.userType,
      user?.role,
      user?.loginRole,
      user?.customerType,
    ]
      .map(normalizeRoleToken)
      .filter(Boolean);

    return roleCandidates[0] || "";
  } catch (error) {
    return "";
  }
});

const showCv = computed(() => userRoleToken.value === "DISTRIBUTOR");

const productSpecifications = computed(() => {
  const specs = product.value?.specifications;
  if (!specs || typeof specs !== "object") return [];
  return Object.entries(specs);
});

const productImageList = computed(() => {
  const p = product.value;
  if (!p) return [];

  const sources = [
    p.files,
    p.productFiles,
    p.images,
    p.product?.files,
    p.product?.productFiles,
    p.product?.images,
  ];

  for (const source of sources) {
    if (!Array.isArray(source) || !source.length) continue;
    const urls = source
      .map((f) => {
        if (typeof f === "string") return f;
        return (
          f?.url ||
          f?.fileUrl ||
          f?.path ||
          f?.location ||
          f?.imageUrl ||
          f?.image ||
          ""
        );
      })
      .filter(Boolean);
    if (urls.length) return urls;
  }

  const single = p.filePath || p.image || p.imageUrl || p.thumbnail || "";
  return single ? [single] : [];
});

// --- DESCRIPTION PARSING ---
// Backend sends description + manufacturing fields bundled into one string,
// delimited by "**", e.g.:
// "...celebratory glow. **Country of Manufacture: India **Manufactured By: ... **Marketed By: ..."
// This splits the free-text description from the structured "Label: value"
// fields so they can render as their own stacked heading/value lines instead
// of running into the paragraph.
// Exact-match line that should render as a bold standalone note.
// Currently only present in pendant descriptions from the backend.
const CHAIN_DISCLAIMER_TEXT =
  "The chain shown is for illustrative purposes only.";

const descriptionParts = computed(() => {
  const raw = String(product.value?.description || "").trim();
  if (!raw) return { mainText: "", noteLines: [], metaFields: [] };

  const segments = raw
    .split(/\*\*/)
    .map((segment) => segment.trim())
    .filter(Boolean);

  if (segments.length <= 1) {
    return { mainText: raw, noteLines: [], metaFields: [] };
  }

  const mainText = segments[0];
  const noteLines = [];
  const metaFields = [];

  for (let i = 1; i < segments.length; i++) {
    const segment = segments[i];

    // Only this exact known disclaimer line renders as a bold note.
    // Any other colon-less segment is ignored (not a recognized field).
    if (segment.toLowerCase().includes(CHAIN_DISCLAIMER_TEXT.toLowerCase())) {
      noteLines.push(segment);
      continue;
    }

    const colonIndex = segment.indexOf(":");
    if (colonIndex === -1) continue;

    const label = segment.slice(0, colonIndex).trim();
    const value = segment.slice(colonIndex + 1).trim();

    if (label && value) {
      metaFields.push({ label, value });
    }
  }

  return { mainText, noteLines, metaFields };
});

const isDescriptionLong = computed(() => {
  const desc = descriptionParts.value.mainText || "";
  // Roughly 2 lines at this column width
  return desc.length > 140 || desc.split("\n").length > 2;
});

// --- UTILITIES ---
function getListPrice(sourceProduct = {}) {
  return (
    sourceProduct?.listPrice ??
    sourceProduct?.list_price ??
    sourceProduct?.product?.listPrice ??
    sourceProduct?.product?.list_price ??
    null
  );
}

function parseComparableAmount(value) {
  if (value === undefined || value === null || value === "") return null;
  const normalizedValue =
    typeof value === "string" ? value.replace(/,/g, "").trim() : value;
  if (normalizedValue === "") return null;
  const parsedNumber = Number(normalizedValue);
  return Number.isFinite(parsedNumber) ? parsedNumber : null;
}

const showStruckMrp = computed(() => {
  const sourceProduct = product.value || {};
  const mrpValue = parseComparableAmount(
    sourceProduct?.mrp ?? sourceProduct?.product?.mrp,
  );
  if (mrpValue === null) return false;

  const listPriceValue = parseComparableAmount(getListPrice(sourceProduct));
  if (listPriceValue === null) return true;

  return mrpValue !== listPriceValue;
});

function formatNumericValue(value) {
  if (value === undefined || value === null || value === "") return null;
  const normalizedValue =
    typeof value === "string" ? value.replace(/,/g, "").trim() : value;
  if (normalizedValue === "") return null;
  const parsedNumber = Number(normalizedValue);
  if (!Number.isFinite(parsedNumber)) return String(value);
  return new Intl.NumberFormat("en-IN", { maximumFractionDigits: 20 }).format(
    parsedNumber,
  );
}

function formatRs(value, sourceProduct = {}) {
  const effectiveValue =
    value === undefined || value === null || value === ""
      ? getListPrice(sourceProduct)
      : value;
  if (
    effectiveValue === undefined ||
    effectiveValue === null ||
    effectiveValue === ""
  )
    return "N/A";
  const formattedValue = formatNumericValue(effectiveValue);
  return `₹${formattedValue ?? effectiveValue}`;
}

function getTenantPayload(response) {
  if (!response) return null;
  if (response?.data?.data) return response.data.data;
  if (response?.data?.tenant) return response.data.tenant;
  if (response?.tenant) return response.tenant;
  if (response?.data) return response.data;
  return response;
}

function getTenantSubDomain(domainName) {
  const normalizedDomain =
    typeof domainName === "string" ? domainName.trim().toLowerCase() : "";
  if (!normalizedDomain) return null;
  const parts = normalizedDomain.split(".").filter(Boolean);
  if (parts.length >= 3 && parts[0] !== "www") return parts[0];
  return normalizedDomain;
}

function getTenantId() {
  const tenantDetails = appStore.tenantDetails || {};
  return (
    tenantDetails.tenantId ||
    tenantDetails.tenantID ||
    tenantDetails.id ||
    tenantDetails.tenant?.id ||
    null
  );
}

// --- TENANT RESOLUTION ---
async function resolveTenant() {
  const tenantId = getTenantId();
  if (tenantId) {
    resolvedTenantId.value = String(tenantId);
    return;
  }

  const domainName = appStore.domainName || window.location.host || "";
  const tenantSubDomain =
    getTenantSubDomain(domainName) || appStore.tenantSubDomain;

  if (!domainName && !tenantSubDomain)
    throw new Error("Tenant domain is missing.");

  let tenantResponse;
  try {
    tenantResponse = await checkTenant(domainName);
  } catch (firstError) {
    if (!tenantSubDomain) throw firstError;
    tenantResponse = await checkTenant(tenantSubDomain);
  }

  const tenantData = getTenantPayload(tenantResponse);
  appStore.setTenantDetails(tenantData);

  const resolvedId = getTenantId();
  if (!resolvedId)
    throw new Error("Tenant ID is missing. Please check tenant configuration.");

  resolvedTenantId.value = String(resolvedId);
}

// --- DATA LOADING ---
async function loadProductDetail() {
  isLoading.value = true;
  errorMessage.value = "";
  cartQuantity.value = 0;
  activeImage.value = "";
  descExpanded.value = false;

  try {
    if (!appStore.baseUrl || !appStore.domainName) {
      appStore.initializeDomainConfig();
    }

    await resolveTenant();

    const productId = String(route.params.productId || "");
    if (!productId) throw new Error("Product ID is missing in route.");

    const response = await getProductDetailById({
      tenantId: resolvedTenantId.value,
      productId,
    });

    const normalized = response?.data || response || {};
    product.value = normalized?.product || normalized;
    syncCartQuantityFromStore();

    if (productImageList.value.length) {
      activeImage.value = productImageList.value[0];
    }
  } catch (error) {
    errorMessage.value =
      error?.response?.data?.statusMessage ||
      error?.message ||
      "Failed to load product detail.";
  } finally {
    isLoading.value = false;
  }
}

onMounted(() => {
  loadProductDetail();
});

watch(
  () => cartStore.items,
  () => {
    syncCartQuantityFromStore();
  },
  { deep: true },
);

watch(
  () => route.params.productId,
  (newProductId, oldProductId) => {
    if (newProductId && newProductId !== oldProductId) {
      loadProductDetail();
    }
  },
);

// --- CART ACTIONS ---
async function handleAddToCart() {
  if (!product.value || isAddingToCart.value) return;

  isAddingToCart.value = true;
  errorMessage.value = "";

  try {
    await cartStore.addProductToCart(product.value);
    syncCartQuantityFromStore();
    showToast.value = true;
    setTimeout(() => (showToast.value = false), 2800);
  } catch (error) {
    console.warn("Add to cart API failed, local cart updated", error);
    syncCartQuantityFromStore();
    showToast.value = true;
    setTimeout(() => (showToast.value = false), 2800);
  } finally {
    isAddingToCart.value = false;
  }
}

// async function handleBuyNow() {
//   if (!product.value) return;

//   // Add to cart first if not already there
//   if (cartQuantity.value === 0) {
//     try {
//       await cartStore.addProductToCart(product.value);
//       syncCartQuantityFromStore();
//     } catch (error) {
//       console.warn("Add to cart before buy now failed", error);
//       syncCartQuantityFromStore();
//     }
//   }

//   const productId = String(product.value?.productId || product.value?.id || "");

//   // NOTE: navigating by path, not by a named route — "PlaceOrder" is not
//   // registered as a route name in your router. Update the path below
//   // (currently "/place-order") if your router defines it differently —
//   // check router/index.js for the actual path to your place-order page.
//   try {
//     await router.push({
//       path: "/place-order",
//       query: { productId, buyNow: "true" },
//     });
//   } catch (navError) {
//     console.error("Navigation to Place Order failed:", navError);
//     errorMessage.value =
//       "Could not open checkout. Please verify the place-order route path.";
//   }
// }

function getCurrentProductKey() {
  const productId = String(product.value?.productId || product.value?.id || "");
  const sku = String(product.value?.sku || "");
  return productId || sku;
}

function syncCartQuantityFromStore() {
  const key = getCurrentProductKey();
  if (!key) {
    cartQuantity.value = 0;
    return;
  }
  const row = cartStore.items.find((item) => item.key === key);
  cartQuantity.value = Number(row?.quantity || 0);
}

async function increaseQty() {
  const key = getCurrentProductKey();
  if (!key) return;
  await cartStore.increaseItemQuantity(key);
  syncCartQuantityFromStore();
}

async function decreaseQty() {
  const key = getCurrentProductKey();
  if (!key) return;
  await cartStore.decreaseItemQuantity(key);
  syncCartQuantityFromStore();
}
</script>

<style scoped>
*,
*::before,
*::after {
  box-sizing: border-box;
  margin: 0;
  padding: 0;
}

.product-detail-root {
  --brand-blue: #003da5;
  --brand-blue-dark: #002d7a;
  --brand-yellow: #ffc72c;
  --charcoal-dark: #0a2240;
  --charcoal-body: #2d3748;
  --soft-bg: #f8fafc;
  --border-light: rgba(0, 61, 165, 0.08);
  --muted-gray: #64748b;
  --pure-white: #ffffff;

  font-family: "Outfit", sans-serif;
  background: var(--soft-bg);
  color: var(--charcoal-body);
  min-height: 100vh;
}

/* TOP NAV */
.top-nav {
  background: var(--pure-white);
  border-bottom: 1px solid var(--border-light);
  padding: 0 24px;
}
.top-nav-inner {
  max-width: 1240px;
  margin: 0 auto;
  height: 52px;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.breadcrumb {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
}
.crumb-link {
  color: var(--muted-gray);
  text-decoration: none;
  transition: color 0.2s;
  font-weight: 500;
}
.crumb-link:hover {
  color: var(--brand-blue);
}
.breadcrumb .sep {
  color: #cbd5e0;
}
.breadcrumb .leaf {
  color: var(--brand-blue);
  font-weight: 700;
}

.back-link {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  color: var(--brand-blue);
  text-decoration: none;
  font-size: 13px;
  font-weight: 700;
  padding: 7px 14px;
  border: 1px solid rgba(0, 61, 165, 0.2);
  border-radius: 8px;
  background: var(--pure-white);
  transition: all 0.2s;
  font-family: "Outfit", sans-serif;
}
.back-link:hover {
  background: var(--brand-blue);
  color: var(--pure-white);
}

/* PAGE BODY */
.page-body {
  max-width: 1240px;
  margin: 0 auto;
  padding: 36px 24px 80px;
}

.status-text {
  color: var(--muted-gray);
  font-size: 14px;
}
.status-text.centered {
  text-align: center;
  padding: 80px 0;
}
.error-text {
  color: #b91c1c;
  font-weight: 600;
  font-size: 14px;
}
.error-text.centered {
  text-align: center;
  padding: 80px 0;
}

/* DETAIL CARD */
.detail-card {
  display: grid;
  grid-template-columns: 460px 1fr;
  gap: 0;
  background: var(--pure-white);
  border: 1px solid var(--border-light);
  border-radius: 20px;
  overflow: hidden;
  box-shadow: 0 8px 40px rgba(0, 61, 165, 0.05);
}

/* IMAGE COLUMN */
.image-col {
  border-right: 1px solid var(--border-light);
  display: flex;
  flex-direction: column;
}

.main-image-wrap {
  width: 100%;
  height: 460px;
  background: #fafafa;
  overflow: hidden;
  position: relative;
}
.main-image-wrap img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
  transition: transform 0.4s ease;
}
.main-image-wrap:hover img {
  transform: scale(1.03);
}

.no-image {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--muted-gray);
  font-size: 13px;
  font-weight: 600;
}

.badge-sale {
  position: absolute;
  top: 16px;
  left: 16px;
  background: var(--brand-blue);
  color: var(--brand-yellow);
  font-size: 11px;
  font-weight: 800;
  padding: 5px 12px;
  border-radius: 6px;
  text-transform: uppercase;
  letter-spacing: 0.75px;
  z-index: 3;
  box-shadow: 0 2px 10px rgba(0, 61, 165, 0.2);
}

.thumbnail-row {
  display: flex;
  gap: 10px;
  padding: 16px;
  background: var(--pure-white);
  border-top: 1px solid var(--border-light);
}
.thumb {
  width: 72px;
  height: 72px;
  border-radius: 10px;
  overflow: hidden;
  border: 2px solid transparent;
  cursor: pointer;
  transition: border-color 0.2s;
  flex-shrink: 0;
}
.thumb.active {
  border-color: var(--brand-blue);
}
.thumb img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

/* DISCLAIMER */
.image-disclaimer {
  display: flex;
  align-items: flex-start;
  gap: 7px;
  padding: 12px 16px;
  background: #fffbeb;
  border-top: 1px solid rgba(255, 199, 44, 0.3);
  /* margin-top: auto;  <-- remove this line */
}
.image-disclaimer svg {
  flex-shrink: 0;
  margin-top: 2px;
  color: #92400e;
}
.image-disclaimer span {
  font-size: 11.5px;
  line-height: 1.6;
  color: #78350f;
}
.image-disclaimer strong {
  font-weight: 700;
}

/* INFO COLUMN */
.info-col {
  padding: 40px;
  display: flex;
  flex-direction: column;
  gap: 22px;
}

.product-header {
  display: flex;
  flex-direction: column;
  gap: 12px;
}
.product-title {
  font-size: 26px;
  font-weight: 800;
  color: var(--charcoal-dark);
  line-height: 1.3;
}
.price-row {
  display: flex;
  align-items: baseline;
  gap: 14px;
}
.net-price {
  font-size: 28px;
  font-weight: 800;
  color: var(--brand-blue);
}
.mrp-price {
  font-size: 16px;
  color: var(--muted-gray);
  text-decoration: line-through;
}

.coin-value-pill {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  align-self: flex-start;
  background: rgba(0, 61, 165, 0.06);
  border: 1px solid rgba(0, 61, 165, 0.15);
  color: var(--charcoal-body);
  font-size: 12.5px;
  font-weight: 600;
  padding: 5px 12px;
  border-radius: 50px;
}

.coin-value-pill strong {
  color: var(--brand-blue);
  font-weight: 800;
}

/* DESCRIPTION */
.description-block {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.product-description {
  font-size: 14.5px;
  color: var(--muted-gray);
  line-height: 1.75;
}

.product-description.clamped {
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.desc-toggle {
  align-self: flex-start;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  background: none;
  border: none;
  padding: 0;
  font-size: 13px;
  font-weight: 700;
  color: var(--brand-blue);
  cursor: pointer;
  font-family: "Outfit", sans-serif;
  transition: opacity 0.2s;
}
.desc-toggle:hover {
  opacity: 0.7;
}

.desc-toggle-icon {
  transition: transform 0.25s ease;
}
.desc-toggle-icon.rotated {
  transform: rotate(180deg);
}

/* PRODUCT META (Country of Manufacture / Manufactured By / Marketed By) —
   parsed out of the description. Each field is stacked: heading/label on
   its own line, value directly beneath — same visual style across all
   three fields, so long addresses wrap cleanly instead of running inline. */
.product-meta-list {
  margin-top: 10px;
  padding-top: 12px;
  border-top: 1px solid #edf2f7;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.product-meta-row {
  display: flex;
  flex-direction: column;
  gap: 3px;
  font-size: 13px;
  line-height: 1.6;
}

.product-meta-label {
  font-weight: 700;
  color: var(--charcoal-dark);
}

.product-meta-value {
  color: var(--muted-gray);
  font-weight: 400;
  margin: 0;
}

/* CART ACTION */
.cart-action-block {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 12px;
  min-height: 46px;
}

.action-buttons-row {
  display: flex;
  gap: 12px;
  align-items: center;
  flex-wrap: wrap;
}

/* BUY NOW — primary, solid brand-blue, hovers to yellow.
   Fixed width + height so it never resizes across states. */
.buy-now-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 9px;
  background: var(--brand-blue);
  border: 2px solid var(--brand-blue);
  color: var(--pure-white);
  border-radius: 50px;
  width: 140px;
  height: 46px;
  font-size: 14.5px;
  font-weight: 700;
  cursor: pointer;
  transition: background 0.25s cubic-bezier(0.4, 0, 0.2, 1),
    border-color 0.25s cubic-bezier(0.4, 0, 0.2, 1),
    color 0.25s cubic-bezier(0.4, 0, 0.2, 1), box-shadow 0.25s ease;
  font-family: "Outfit", sans-serif;
  flex: 0 0 auto;
}
.buy-now-btn:hover {
  background: var(--brand-yellow);
  border-color: var(--brand-yellow);
  color: var(--charcoal-dark);
  box-shadow: 0 4px 16px rgba(255, 199, 44, 0.35);
}

/* ADD TO CART — secondary, outlined. Same fixed height, content-hugging width */
.add-to-cart-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 9px;
  background: var(--pure-white);
  border: 2px solid rgba(0, 61, 165, 0.25);
  color: var(--brand-blue);
  border-radius: 50px;
  height: 46px;
  padding: 0 22px;
  font-size: 14.5px;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
  font-family: "Outfit", sans-serif;
  flex: 0 0 auto;
}
.add-to-cart-btn:hover:not(:disabled) {
  border-color: var(--brand-blue);
  background: rgba(0, 61, 165, 0.05);
}
.add-to-cart-btn.adding {
  opacity: 0.7;
  cursor: not-allowed;
}
.add-to-cart-btn:disabled {
  background: #e8e8e8;
  border-color: #e8e8e8;
  cursor: not-allowed;
}

.out-of-stock-pill {
  display: inline-flex;
  align-items: center;
  background: #fef2f2;
  color: #b91c1c;
  border: 1px solid #fecaca;
  border-radius: 50px;
  padding: 11px 24px;
  font-size: 14px;
  font-weight: 700;
}

.delivery-timeline {
  margin-top: 10px;
  padding-top: 12px;
  border-top: 1px solid #edf2f7;
  display: flex;
  align-items: center;
  gap: 9px;
}

.delivery-timeline svg {
  flex-shrink: 0;
  color: var(--brand-blue);
}

.delivery-timeline span {
  font-size: 13px;
  color: var(--muted-gray);
}

.delivery-timeline span strong {
  color: var(--charcoal-dark);
  font-weight: 700;
}

/* QTY CONTROLS */
.qty-state {
  display: flex;
  align-items: center;
  gap: 14px;
  flex-wrap: wrap;
}
.qty-controls {
  display: inline-flex;
  align-items: center;
  border: 2px solid var(--brand-blue);
  border-radius: 50px;
  overflow: hidden;
  background: var(--pure-white);
}
.qty-btn {
  width: 42px;
  height: 42px;
  border: none;
  background: transparent;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--brand-blue);
  transition: background 0.2s;
}
.qty-btn:hover {
  background: rgba(0, 61, 165, 0.06);
}
.qty-count {
  min-width: 42px;
  text-align: center;
  font-size: 16px;
  font-weight: 800;
  color: var(--brand-blue);
  border-left: 1px solid rgba(0, 61, 165, 0.15);
  border-right: 1px solid rgba(0, 61, 165, 0.15);
  padding: 0 4px;
  line-height: 42px;
  font-family: "Outfit", sans-serif;
}
.qty-label {
  font-size: 13px;
  font-weight: 600;
  color: var(--muted-gray);
}

/* TOAST */
.toast {
  background: #f0fdf4;
  border: 1px solid #bbf7d0;
  color: #16a34a;
  border-radius: 8px;
  padding: 11px 16px;
  font-size: 13px;
  font-weight: 600;
  display: inline-block;
}
.toast-fade-enter-active,
.toast-fade-leave-active {
  transition: opacity 0.3s ease, transform 0.3s ease;
}
.toast-fade-enter-from,
.toast-fade-leave-to {
  opacity: 0;
  transform: translateY(6px);
}

.divider {
  height: 1px;
  background: #edf2f7;
}

/* SPECS */
.specs-block {
  display: flex;
  flex-direction: column;
  gap: 14px;
}
.specs-title {
  font-size: 13px;
  font-weight: 800;
  color: var(--charcoal-dark);
  text-transform: uppercase;
  letter-spacing: 0.75px;
}
.spec-list {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 10px;
}
.spec-item {
  background: #f8fafc;
  border: 1px solid var(--border-light);
  border-radius: 8px;
  padding: 10px 14px;
  display: flex;
  flex-direction: column;
  gap: 3px;
}
.spec-key {
  font-size: 11px;
  color: var(--muted-gray);
  text-transform: uppercase;
  letter-spacing: 0.5px;
  font-weight: 700;
}
.spec-value {
  font-size: 13px;
  font-weight: 700;
  color: var(--charcoal-dark);
}

.product-note {
  font-size: 13.5px;
  font-weight: 700;
  color: var(--charcoal-dark);
  line-height: 1.6;
}

/* RESPONSIVE */
@media (max-width: 960px) {
  .detail-card {
    grid-template-columns: 1fr;
  }
  .image-col {
    border-right: none;
    border-bottom: 1px solid var(--border-light);
  }
  .info-col {
    padding: 28px;
  }
}
@media (max-width: 600px) {
  .spec-list {
    grid-template-columns: 1fr;
  }
  .top-nav-inner {
    height: auto;
    padding: 12px 0;
    flex-wrap: wrap;
    gap: 8px;
  }
  .page-body {
    padding: 24px 16px 60px;
  }
  .action-buttons-row {
    flex-direction: column;
    align-items: stretch;
  }
  .add-to-cart-btn,
  .buy-now-btn {
    width: 100%;
  }
}
</style>
